import { Navigate, useLocation, useNavigate } from "@solidjs/router";
import { createSignal } from "solid-js";
import { useAuth } from "../auth/AuthContext.tsx";
import { ErrorBanner } from "../components/ErrorBanner.tsx";
import { QrCode } from "../components/QrCode.tsx";

interface EnrollState {
  accountId: string;
  totpSecret: string;
  provisioningUri: string;
}

/** Second step of signup: scan the TOTP secret and confirm the first code. */
export function ConfirmTotp() {
  const { client } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? null) as EnrollState | null;

  const [code, setCode] = createSignal("");
  const [error, setError] = createSignal<unknown | null>(null);
  const [busy, setBusy] = createSignal(false);

  // Reaching this page without enrollment context (direct URL / refresh) is
  // recoverable — send the user back to /register for a fresh secret.
  if (!state) return <Navigate href="/register" />;
  const enroll = state;

  async function onSubmit(e: Event) {
    e.preventDefault();
    if (busy()) return;
    setError(null);
    setBusy(true);
    try {
      await client.confirmTotp({ accountId: enroll.accountId, code: code() });
      navigate("/login", { replace: true });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="auth-shell">
      <form onSubmit={onSubmit} class="card auth-card">
        <h1>Set up 2FA</h1>
        <p class="muted">
          Scan this QR code with your authenticator app (Authy, 1Password, Google Authenticator),
          then enter the 6-digit code it shows.
        </p>
        <ErrorBanner error={error()} />
        <QrCode value={enroll.provisioningUri} size={184} />
        <p class="muted" style={{ "margin-bottom": "0.25rem" }}>
          Can't scan? Enter this secret manually
        </p>
        <pre class="totp-uri">{enroll.totpSecret}</pre>
        <label for="totp">6-digit code</label>
        <input
          id="totp"
          inputmode="numeric"
          pattern="[0-9]{6}"
          autocomplete="one-time-code"
          value={code()}
          onInput={(e) => setCode(e.currentTarget.value.replace(/\D/g, "").slice(0, 6))}
          required
        />
        <button type="submit" class="primary auth-submit" disabled={busy()}>
          {busy() ? "Confirming…" : "Confirm and continue"}
        </button>
      </form>
    </div>
  );
}
